// src/components/AskPsd/services/api.js
// Adapted from psd-ai-ui's services/api.js — uses native fetch instead of
// axios (the portal has no axios dependency; it's a GraphQL/Apollo app, and
// these are plain REST endpoints on psd-ai-api, so fetch needs nothing extra).

import { ASK_PSD_API_BASE_URL } from '../../../config/envConfig';
import { logger } from './logger';

// Sent in the request body/query params (not as a header — the backend
// requires either `domain` or `projectId` in the payload itself, and
// rejects requests missing both with a 400).
const ASK_PSD_DOMAIN = 'PSD_IBP_O9';

// Individual askQuestion/pollStatus calls should never hang forever — a
// dropped connection or a hung proxy would otherwise leave the UI stuck on
// "Processing…" indefinitely with no error ever surfaced.
const REQUEST_TIMEOUT_MS = 20000;

// Thrown for both non-2xx responses and network/timeout failures so callers
// can rely on a consistent shape (`.status` is null for network/timeout
// errors, which callers use to tell "server said no" apart from "never got
// a response at all").
export class AskPsdApiError extends Error {
  constructor(message, status = null) {
    super(message);
    this.name = 'AskPsdApiError';
    this.status = status;
  }
}

async function fetchWithTimeout(url, options, externalSignal) {
  const controller = new AbortController();
  const onExternalAbort = () => controller.abort();
  externalSignal?.addEventListener('abort', onExternalAbort);
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (err) {
    if (err.name === 'AbortError') {
      throw new AskPsdApiError(
        externalSignal?.aborted
          ? 'Request was cancelled.'
          : 'Request timed out. Please try again.'
      );
    }
    // Network failure (offline, DNS, CORS) — no HTTP status to report.
    throw new AskPsdApiError(
      'Network error. Please check your connection and try again.'
    );
  } finally {
    clearTimeout(timeoutId);
    externalSignal?.removeEventListener('abort', onExternalAbort);
  }
}

async function parseJsonOrThrow(response) {
  if (!response.ok) {
    // Try to surface a server-provided error message before falling back
    // to a generic status-based one.
    let serverMessage = null;
    try {
      const body = await response.json();
      serverMessage =
        body?.message || body?.error || body?.errorMessage || null;
    } catch {
      // Response body wasn't JSON (or was empty) — ignore, use fallback below.
    }
    throw new AskPsdApiError(
      serverMessage || `Request failed with status ${response.status}`,
      response.status
    );
  }
  return response.json();
}

export function createAskPsdApi(getToken, renewToken) {
  const authHeaders = () => {
    const token = getToken?.();
    return {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    };
  };

  // On a 401, try renewing the Okta token once and retrying the same request
  // with the fresh one — otherwise a token that expires mid-poll surfaces as
  // a generic failure instead of silently recovering.
  const requestWithAuthRetry = async (url, options, signal) => {
    let response = await fetchWithTimeout(
      url,
      { ...options, headers: authHeaders() },
      signal
    );

    if (response.status === 401 && renewToken) {
      try {
        await renewToken();
      } catch (renewError) {
        logger.error('Token renewal failed after a 401 response', renewError);
        return response;
      }
      response = await fetchWithTimeout(
        url,
        { ...options, headers: authHeaders() },
        signal
      );
    }

    return response;
  };

  // `session` is optional: { sessionId } continues an existing session,
  // { sessionTitle } auto-creates a new one on this first message (the
  // server returns its sessionId in the response), or omit entirely to
  // stay fully stateless (no session created) — backward-compatible.
  const askQuestion = async (question, session, signal) => {
    const body = { domain: ASK_PSD_DOMAIN, question };
    if (session?.sessionId) {
      body.sessionId = session.sessionId;
    } else if (session?.sessionTitle !== undefined) {
      body.sessionTitle = session.sessionTitle;
    }

    const response = await requestWithAuthRetry(
      `${ASK_PSD_API_BASE_URL}/ask`,
      { method: 'POST', body: JSON.stringify(body) },
      signal
    );
    const data = await parseJsonOrThrow(response);
    if (!data.jobId) {
      logger.error('askQuestion succeeded but response had no jobId', data);
      throw new AskPsdApiError(
        'Unexpected response from the server. Please try again.'
      );
    }
    return data;
  };

  const pollStatus = async (jobId, signal) => {
    const response = await requestWithAuthRetry(
      `${ASK_PSD_API_BASE_URL}/status/${jobId}`,
      { method: 'GET' },
      signal
    );
    return parseJsonOrThrow(response);
  };

  // ── Session history — persist/resume conversations (see psd-ai-api's
  // "Chat Session History" API) ──────────────────────────────────────────
  const createSession = async (title, signal) => {
    const body = { domain: ASK_PSD_DOMAIN };
    if (title) {
      body.title = title;
    }
    const response = await requestWithAuthRetry(
      `${ASK_PSD_API_BASE_URL}/sessions`,
      { method: 'POST', body: JSON.stringify(body) },
      signal
    );
    return parseJsonOrThrow(response);
  };

  const listSessions = async ({ page = 0, size = 20 } = {}, signal) => {
    const params = new URLSearchParams({ domain: ASK_PSD_DOMAIN, page, size });
    const response = await requestWithAuthRetry(
      `${ASK_PSD_API_BASE_URL}/sessions?${params}`,
      { method: 'GET' },
      signal
    );
    return parseJsonOrThrow(response);
  };

  const getSession = async (sessionId, signal) => {
    const response = await requestWithAuthRetry(
      `${ASK_PSD_API_BASE_URL}/sessions/${sessionId}`,
      { method: 'GET' },
      signal
    );
    return parseJsonOrThrow(response);
  };

  const getSessionTurns = async (sessionId, signal) => {
    const response = await requestWithAuthRetry(
      `${ASK_PSD_API_BASE_URL}/sessions/${sessionId}/turns`,
      { method: 'GET' },
      signal
    );
    return parseJsonOrThrow(response);
  };

  const deleteSession = async (sessionId, signal) => {
    const response = await requestWithAuthRetry(
      `${ASK_PSD_API_BASE_URL}/sessions/${sessionId}`,
      { method: 'DELETE' },
      signal
    );
    if (!response.ok) {
      await parseJsonOrThrow(response);
    }
  };

  return {
    askQuestion,
    pollStatus,
    createSession,
    listSessions,
    getSession,
    getSessionTurns,
    deleteSession,
  };
}
